import { StatusCodes } from 'http-status-codes'
import { ZodError, z } from 'zod'

import { ResidentAppError } from '../../common/error'
import { ResidentAppErrorCode } from '../../common/types'

import { sleep } from 'utils'

const BASE_PATH = '/api'

const handleResponse = async <T>(res: Response, schema: z.ZodType<T>) => {
  try {
    const data = await res.text() // this might also throw

    if (res.status === StatusCodes.OK) {
      const parsed = JSON.parse(data)

      if (parsed.warning) {
        localStorage.setItem('loginDevWarning', parsed.warning)
      }

      return schema.parse(parsed)
    }

    if (res.status === StatusCodes.NO_CONTENT) {
      return schema.parse(undefined)
    }

    // Handle 504 Gateway Timeout
    if (res.status === StatusCodes.GATEWAY_TIMEOUT) {
      throw ResidentAppError.ClientError('GATEWAY_TIMEOUT', {
        message:
          'Kyselyä ei voitu suorittaa ja se aikakatkaistiin. Ota yhteyttä Pandian asiakaspalveluun ongelman selvittämiseksi.',
        errorDetails: { url: res.url, status: res.status },
      })
    }

    const payload = JSON.parse(data) as {
      error: ResidentAppErrorCode
      message?: string
      eventId?: string
    }
    const error = payload?.error
    const responseMessage =
      typeof payload?.message === 'string' ? payload.message : undefined
    const eventId =
      typeof payload?.eventId === 'string' ? payload.eventId : undefined

    if (res.status === StatusCodes.UNAUTHORIZED) {
      if (
        [
          ResidentAppErrorCode.UNAUTHORIZED,
          ResidentAppErrorCode.AUTH_ERROR,
        ].includes(error)
      ) {
        window.location.assign('/login?error=login_expired')

        throw ResidentAppError.ClientError('UNAUTHORIZED', {
          message: 'User session expired',
        })
      } else if (
        error === ResidentAppErrorCode.IMPERSONATION_WRITE_ACCESS_DENIED
      ) {
        throw ResidentAppError.ClientError(
          'IMPERSONATION_WRITE_ACCESS_DENIED',
          {
            message: 'impersonator write access denied',
          }
        )
      }
    }

    throw ResidentAppError.ClientError(error, {
      message: responseMessage ?? `API returned an error: ${error}`,
      errorDetails: {
        error,
        url: res.url,
        status: res.status,
        response: payload,
        eventId,
      },
    })
  } catch (e: unknown) {
    if (e instanceof ResidentAppError) {
      throw e
    }

    if (e instanceof ZodError) {
      throw ResidentAppError.ClientError('INVALID_RESPONSE', {
        message: 'Failed to validate API response',
        originatingError: e,
      })
    }

    throw ResidentAppError.ClientError('UNEXPECTED', {
      message: 'Failed to parse API error response',
      originatingError: e,
    })
  }
}

export const get = async <T>(
  path: string,
  schema: z.ZodType<T>
): Promise<T> => {
  const res = await fetch(BASE_PATH + path, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  })
  return handleResponse(res, schema)
}

export const post = async <T>(
  path: string,
  body: Record<string, unknown>,
  schema: z.ZodType<T>
): Promise<T> => {
  // Avoid flickering submitting states
  await sleep(1000)

  const res = await fetch(BASE_PATH + path, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })
  return handleResponse(res, schema).catch((e) => {
    return Promise.reject(e)
  })
}

export const patch = async <T>(
  path: string,
  body: Record<string, unknown>,
  schema: z.ZodType<T>
): Promise<T> => {
  await sleep(1000)

  const res = await fetch(BASE_PATH + path, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })
  return handleResponse(res, schema).catch((e) => {
    return Promise.reject(e)
  })
}

export const del = async <T>(
  path: string,
  schema: z.ZodType<T>
): Promise<T> => {
  const res = await fetch(BASE_PATH + path, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
    },
  })

  return handleResponse(res, schema)
}
